import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import ListHoverItem from './ListHoverItem';
import ShareLinks from './ShareLinks';

// Deterministically pick the current section: the last heading whose top has
// scrolled above the line just below the sticky header. Always yields exactly
// one active id (defaults to the first), so the highlight never flickers or
// disappears — including right after clicking a section link.
const useActiveHeading = (headings, offset) => {
  const [activeId, setActiveId] = useState(headings?.[0]?.id || '');
  useEffect(() => {
    if (typeof window === 'undefined' || !headings?.length) return undefined;
    const onScroll = () => {
      // Sit the detection line near where a heading rests after anchor
      // navigation (its scroll-mt), so clicking a link marks that section.
      const line = (offset || 0) + 140;
      let current = headings[0].id;
      headings.forEach((h) => {
        const el = document.getElementById(h.id);
        if (el && el.getBoundingClientRect().top <= line) current = h.id;
      });
      setActiveId(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [headings, offset]);
  return activeId;
};

// Keep the nav clear of the sticky site header.
const useHeaderOffset = () => {
  const [top, setTop] = useState(0);
  useEffect(() => {
    const measure = () => {
      const header = document.getElementById('header');
      setTop(header ? header.offsetHeight : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return top;
};

const CaseStudyNav = ({ headings, shareUrl, shareTitle, shareSummary, variant }) => {
  const top = useHeaderOffset();
  const activeId = useActiveHeading(headings, top);

  // Keep the active item in view within the horizontal (mobile) bar, left-aligned:
  // scroll so the active item's left edge sits at the bar's content start (just
  // inside the px-6 inset), rather than centred.
  const listRef = useRef(null);
  useEffect(() => {
    const container = listRef.current;
    const el = container?.querySelector('[aria-current]');
    if (!container || !el) return;
    const styles = window.getComputedStyle(container);
    const padLeft = parseFloat(styles.paddingLeft) || 0;
    const delta =
      el.getBoundingClientRect().left - container.getBoundingClientRect().left - padLeft;
    container.scrollBy({ left: delta, behavior: 'smooth' });
  }, [activeId]);

  if (!headings?.length) return null;

  const share = { shareUrl, shareTitle, shareSummary };

  // Vertical rail (xl+). Mirrors the site's "In this Page:" jump-links style
  // (serif body type + the ListHoverItem arrow) at a reduced size, with the
  // arrow pinned to the active section. Centred in the gutter so it sits between
  // the page edge and the reading column.
  if (variant === 'vertical') {
    return (
      <nav aria-label="In this page" className="sticky xl:mx-auto xl:w-[19rem]" style={{ top: top + 16 }}>
        <h3 className="text-sm mb-4">In this Page:</h3>
        <div className="divide-y divide-color-base divide-opacity-20 border-y border-color-base border-opacity-20">
          {headings.map((h) => (
            <a
              key={h.id}
              href={`#${h.id}`}
              aria-current={activeId === h.id ? 'location' : undefined}
              className={`hover-row block overflow-hidden py-2 no-underline text-xs leading-snug ${
                activeId === h.id ? 'is-active text-color-base' : 'text-color-base/70'
              }`}
            >
              <ListHoverItem>{h.text}</ListHoverItem>
            </a>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-6">
          <ShareLinks {...share} size="h-9 w-9" />
        </div>
      </nav>
    );
  }

  // Horizontal bar (below xl): a slim dark bar pinned under the header. No label
  // and no share controls (mobile share lives in the hero). The active section
  // gets an off-white tab line along the bar's bottom edge — the vertical
  // padding lives on the anchors so the border sits flush with the bar bottom,
  // and inactive items keep a transparent border to avoid any layout shift.
  // The list scrolls horizontally (swipe / auto-scroll) with its scrollbar
  // hidden via `no-scrollbar`, so the overlay scrollbar doesn't render as a
  // faint line along the bar's bottom edge.
  return (
    <nav
      aria-label="In this page"
      className="xl:hidden sticky z-40 bg-soft-black text-off-white"
      style={{ top }}
    >
      <ul ref={listRef} className="no-scrollbar max-w-9xl px-6 mx-auto flex items-stretch gap-6 overflow-x-auto whitespace-nowrap">
        {headings.map((h) => (
          <li key={h.id} className="shrink-0 flex">
            <a
              href={`#${h.id}`}
              aria-current={activeId === h.id ? 'location' : undefined}
              className={`flex items-center font-trade text-[0.8rem] no-underline py-3 border-b-2 ${
                activeId === h.id
                  ? 'border-off-white opacity-100'
                  : 'border-transparent opacity-80 hover:opacity-100'
              }`}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

CaseStudyNav.propTypes = {
  headings: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, text: PropTypes.string })
  ),
  shareUrl: PropTypes.string,
  shareTitle: PropTypes.string,
  shareSummary: PropTypes.string,
  variant: PropTypes.oneOf(['vertical', 'horizontal']),
};

export default CaseStudyNav;
